import { set } from 'lodash';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { NumberInput } from '@hivebrite/design-system/Inputs';

import RadiosInput from 'hb-builder/components/common/SettingsLibrary/ComposedInputs/RadiosInput';
import InputTitle from 'hb-builder/components/common/SettingsLibrary/SettingsDecoration/InputTitle';
import type { BodyConfig } from 'hb-builder/types';
import type { DocumentItem } from 'hb-react/backoffice/types/builder/builderDocument';

type OnChangeValueArg = string | number;

export type Updater = (args: {
  item: DocumentItem;
  value: OnChangeValueArg;
}) => DocumentItem;

interface WidthProps
  extends Pick<BodyConfig, 'canBeFullWidth' | 'defaultWidth'> {
  item: DocumentItem;
  onChange: (args: { value: OnChangeValueArg; updater: Updater }) => void;
}

const Width = ({
  canBeFullWidth,
  defaultWidth,
  item,
  onChange,
}: WidthProps) => {
  const defaultBodyWidth = defaultWidth || '100%';

  const initialValue = item?.attributes?.bodyWidth;

  const initialValueIfNotFullWidth =
    initialValue === 'auto' ? 'recommendation' : 'custom';

  const [radioValue, setRadioValue] = useState(
    initialValue === '100%' ? 'full' : initialValueIfNotFullWidth,
  );

  const [customValue, setCustomValue] = useState(
    radioValue === 'custom' && initialValue,
  );

  const handleChange = (value: string) => {
    setRadioValue(value);
  };

  const handleInputChange = (event) => {
    if (radioValue !== 'custom') {
      setRadioValue('custom');
    }

    setCustomValue(event.target.value);
  };

  useEffect(() => {
    const valueWhenNotCustom =
      radioValue === 'recommendation' ? 'auto' : '100%';

    onChange({
      value:
        radioValue === 'custom'
          ? parseInt(customValue, 10)
          : valueWhenNotCustom,
      updater: ({ item, value }) => {
        set(item, 'attributes.bodyWidth', value);

        if (item.children) {
          item.children.forEach((section) =>
            set(
              section,
              'attributes.sectionWidth',
              value === 'auto' ? '100%' : value,
            ),
          );
        }

        return item;
      },
    });
  }, [customValue, radioValue]);

  return (
    <Wrapper flexDirection={canBeFullWidth ? 'column' : 'row'}>
      <InputTitle boxProps={{ mb: 3 }} label={__('Content width')} />
      {canBeFullWidth ? (
        <RadiosInput
          direction="vertical"
          name="content-width"
          onChange={handleChange}
          options={[
            {
              description: __(
                'Your content will always cover 100% of the screen size',
              ),
              value: 'full',
              label: __('Full width (100%)'),
            },
            {
              description: (
                <DescriptionWrapper>
                  <Description>
                    {__('Define a custom maximum width for your content')}
                  </Description>
                  <NumberInput
                    dataTestId={`settingsNumber-${item.id}`}
                    ariaLabel={__(
                      'Define a custom maximum width for your content',
                    )}
                    disabled={radioValue === 'full'}
                    id={`settingsNumber-${item.id}`}
                    name={`settingsNumber-${item.id}`}
                    onChange={handleInputChange}
                    value={
                      customValue === '100%' ? defaultBodyWidth : customValue
                    }
                    variant="small"
                    width="50%"
                  />
                </DescriptionWrapper>
              ),
              value: 'custom',
              label: __('Custom width (in pixels)'),
            },
            {
              description: __(
                'Your content will have the same alignment than other page of your community',
              ),
              value: 'recommendation',
              label: __('Recommended width'),
            },
          ].filter(Boolean)}
          value={radioValue}
        />
      ) : (
        <NumberInput
          dataTestId={`settingsNumber-${item.id}`}
          id={`settingsNumber-${item.id}`}
          name={`settingsNumber-${item.id}`}
          onChange={handleInputChange}
          value={customValue}
          variant="small"
          width="50%"
        />
      )}
    </Wrapper>
  );
};

export default Width;

const Description = styled(Box).attrs(() => ({
  color: 'dove',
  mb: 3,
}))``;

const DescriptionWrapper = styled(Box).attrs(() => ({
  flexDirection: 'column',
}))``;

const Wrapper = styled(Box).attrs(() => ({
  flexDirection: 'column',
  mb: 4,
}))``;
